import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Modal, TextField, Button, Grid, MenuItem, IconButton, 
    Chip, Autocomplete, FormControl, InputLabel, Select, CircularProgress,
    Card, CardMedia, FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CollectionsIcon from '@mui/icons-material/Collections';
import { RoomService } from '../api/services/roomService';
import { MediaService } from '../api/services/mediaService';
import { useCurrency } from '../utils/currency';

export default function RoomFormModal({ open, onClose, onSave, editingRoom = null, data = null }) {
    const { currencySymbol } = useCurrency();
    const targetRoom = editingRoom || data;
    const [formData, setFormData] = useState({
        roomNumber: '',
        floorId: '',
        wingId: '',
        roomTypeId: '',
        baseRate: '',
        maxAdults: 2,
        maxKids: 0,
        amenities: [],
        images: [],
        status: 'Available'
    });

    const [roomTypes, setRoomTypes] = useState([]);
    const [floors, setFloors] = useState([]);
    const [wings, setWings] = useState([]);
    const [amenitiesList, setAmenitiesList] = useState([]);
    const [isLoadingSetup, setIsLoadingSetup] = useState(false);
    const [isUploadingImages, setIsUploadingImages] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    useEffect(() => {
        const fetchSetupData = async () => {
            if (!open) return;
            
            setIsLoadingSetup(true);
            try {
                const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
                const tenantId = authData.tenantId || '1';

                const [typesRes, floorsRes, wingsRes, amenitiesRes] = await Promise.all([
                    RoomService.getRoomTypes(),
                    RoomService.getFloors(),
                    RoomService.getWings(),
                    RoomService.getAmenities()
                ]);

                setRoomTypes(typesRes.data || typesRes || []);
                setFloors(floorsRes.data || floorsRes || []);
                setWings(wingsRes.data || wingsRes || []);
                setAmenitiesList(amenitiesRes.data || amenitiesRes || []);
            } catch (error) {
                console.error("Failed to fetch room setup data:", error);
            } finally {
                setIsLoadingSetup(false);
            }
        };

        fetchSetupData();
    }, [open]);

    useEffect(() => {
        if (targetRoom) {
            const initialImages = Array.isArray(targetRoom.images)
                ? targetRoom.images
                : (targetRoom.image ? [targetRoom.image] : (Array.isArray(targetRoom.photos) ? targetRoom.photos : []));

            // Resolve roomTypeId
            let resolvedRoomTypeId = targetRoom.roomTypeId || (typeof targetRoom.roomType === 'object' ? targetRoom.roomType?.id : '') || (typeof targetRoom.type === 'object' ? targetRoom.type?.id : '');
            if (!resolvedRoomTypeId && (typeof targetRoom.type === 'string' || typeof targetRoom.roomType === 'string')) {
                const typeStr = targetRoom.type || targetRoom.roomType;
                const matchedType = roomTypes.find(t => t.id === typeStr || t.name?.toLowerCase() === typeStr?.toLowerCase());
                if (matchedType) resolvedRoomTypeId = matchedType.id;
                else resolvedRoomTypeId = typeStr;
            }

            // Resolve floorId
            let resolvedFloorId = targetRoom.floorId || (typeof targetRoom.floor === 'object' ? targetRoom.floor?.id : '');
            if (!resolvedFloorId && targetRoom.floor) {
                const floorVal = targetRoom.floor;
                const matchedFloor = floors.find(f => f.id === floorVal || String(f.number) === String(floorVal) || f.label?.toLowerCase() === String(floorVal)?.toLowerCase());
                if (matchedFloor) resolvedFloorId = matchedFloor.id;
                else resolvedFloorId = floorVal;
            }

            // Resolve wingId
            let resolvedWingId = targetRoom.wingId || (typeof targetRoom.wing === 'object' ? targetRoom.wing?.id : '');
            if (!resolvedWingId && targetRoom.wing) {
                const wingVal = targetRoom.wing;
                const matchedWing = wings.find(w => w.id === wingVal || w.name?.toLowerCase() === String(wingVal)?.toLowerCase());
                if (matchedWing) resolvedWingId = matchedWing.id;
                else resolvedWingId = wingVal;
            }

            // Resolve amenities
            let initialAmenities = [];
            if (Array.isArray(targetRoom.amenities)) {
                initialAmenities = targetRoom.amenities.map(a => typeof a === 'object' ? (a.name || a.label || '') : a);
            } else if (Array.isArray(targetRoom.features)) {
                initialAmenities = targetRoom.features.map(a => typeof a === 'object' ? (a.name || a.label || '') : a);
            }

            setFormData({
                ...targetRoom,
                roomNumber: targetRoom.roomNumber || targetRoom.number || '',
                floorId: resolvedFloorId || '',
                wingId: resolvedWingId || '',
                roomTypeId: resolvedRoomTypeId || '',
                baseRate: targetRoom.baseRate || targetRoom.rate || '',
                maxAdults: targetRoom.maxAdults ?? targetRoom.maxOccupancy ?? 2,
                maxKids: targetRoom.maxKids ?? 0,
                amenities: initialAmenities,
                images: initialImages,
                status: typeof targetRoom.status === 'object' ? (targetRoom.status?.name || 'Available') : (targetRoom.status || 'Available')
            });
        } else {
            setFormData({
                roomNumber: '',
                floorId: '',
                wingId: '',
                roomTypeId: '',
                baseRate: '',
                maxAdults: 2,
                maxKids: 0,
                amenities: [],
                images: [],
                status: 'Available'
            });
        }
        setUploadError(null);
    }, [targetRoom, open, roomTypes, floors, wings]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updates = { [name]: value };
            
            // Auto-fill configuration when a Room Type is selected
            if (name === 'roomTypeId') {
                const selectedType = roomTypes.find(t => t.id === value);
                if (selectedType) {
                    if (selectedType.baseRate !== undefined) updates.baseRate = selectedType.baseRate;
                    if (selectedType.maxAdults !== undefined) updates.maxAdults = selectedType.maxAdults;
                    if (selectedType.maxKids !== undefined) updates.maxKids = selectedType.maxKids;
                    if (selectedType.defaultAmenities !== undefined) updates.amenities = selectedType.defaultAmenities;
                }
            }
            
            return { ...prev, ...updates };
        });
    };

    const handleAmenitiesChange = (event, newValue) => {
        setFormData(prev => ({ ...prev, amenities: newValue }));
    };

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        setIsUploadingImages(true);
        setUploadError(null);

        try {
            const uploadPromises = files.map(async (file) => {
                const result = await MediaService.uploadFile(file);
                return result?.url || result?.publicUrl || result;
            });

            const uploadedUrls = await Promise.all(uploadPromises);
            const validUrls = uploadedUrls.filter(url => typeof url === 'string' && url.length > 0);

            setFormData(prev => ({
                ...prev,
                images: [...(prev.images || []), ...validUrls]
            }));
        } catch (error) {
            console.error("Failed to upload room images:", error);
            setUploadError(error.message || 'Failed to upload one or more images. Please try again.');
        } finally {
            setIsUploadingImages(false);
            // Reset input value to allow selecting same file again
            if (event.target) event.target.value = '';
        }
    };

    const handleRemoveImage = (indexToRemove) => {
        setFormData(prev => ({
            ...prev,
            images: (prev.images || []).filter((_, idx) => idx !== indexToRemove)
        }));
    };

    const handleSubmit = () => {
        if (onSave) {
            const payload = { ...formData };
            // Remove populated objects or duplicate fields that backend doesn't expect
            delete payload.type;
            delete payload.floor;
            delete payload.wing;
            delete payload.roomType;
            payload.images = Array.isArray(formData.images) ? formData.images : [];
            
            onSave(payload);
        }
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '92%', sm: 650 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
                maxHeight: '90vh', overflowY: 'auto'
            }}>
                <IconButton sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} disabled={isLoadingSetup || isUploadingImages}>
                    <CloseIcon />
                </IconButton>

                <Typography variant="h6" fontWeight="bold" mb={3}>
                    {editingRoom ? `Edit Room ${formData.roomNumber}` : 'Create New Room'}
                </Typography>

                {isLoadingSetup ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }} color="text.secondary">Loading property setup data...</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Room Number"
                                name="roomNumber"
                                value={formData.roomNumber}
                                onChange={handleChange}
                                disabled={!!editingRoom}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Room Type</InputLabel>
                                <Select name="roomTypeId" value={formData.roomTypeId} label="Room Type" onChange={handleChange}>
                                    {roomTypes.map(t => (
                                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Floor</InputLabel>
                                <Select name="floorId" value={formData.floorId} label="Floor" onChange={handleChange}>
                                    {floors.map(f => (
                                        <MenuItem key={f.id} value={f.id}>{f.label || `Floor ${f.number}`}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Wing</InputLabel>
                                <Select name="wingId" value={formData.wingId} label="Wing" onChange={handleChange}>
                                    {wings.map(w => (
                                        <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Max Adults"
                                name="maxAdults"
                                type="number"
                                value={formData.maxAdults}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Max Kids"
                                name="maxKids"
                                type="number"
                                value={formData.maxKids}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={`Base Rate (${currencySymbol})`}
                                name="baseRate"
                                type="number"
                                value={formData.baseRate}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select 
                                    name="status" 
                                    value={formData.status} 
                                    label="Status" 
                                    onChange={handleChange}
                                    disabled={editingRoom?.status === 'OOO' || editingRoom?.status === 'Out of Order'}
                                >
                                    <MenuItem value="Available">Available</MenuItem>
                                    <MenuItem value="Occupied">Occupied</MenuItem>
                                    <MenuItem value="Cleaning">Cleaning</MenuItem>
                                    <MenuItem value="Repair">Repair</MenuItem>
                                    {(editingRoom?.status === 'OOO' || editingRoom?.status === 'Out of Order') && (
                                        <MenuItem value={editingRoom.status}>{editingRoom.status === 'OOO' ? 'OOO' : 'Out of Order'}</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={amenitiesList.map(a => a.name)}
                                value={formData.amenities || []}
                                onChange={handleAmenitiesChange}
                                renderInput={(params) => <TextField {...params} label="Features & Amenities" />}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip label={option} {...getTagProps({ index })} size="small" />
                                    ))
                                }
                            />
                        </Grid>

                        {/* Room Images Section */}
                        <Grid item xs={12}>
                            <Box sx={{ 
                                mt: 1, 
                                p: 2, 
                                border: '1px dashed #cbd5e1', 
                                borderRadius: 2, 
                                bgcolor: '#f8fafc' 
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CollectionsIcon color="primary" fontSize="small" />
                                        <Typography variant="subtitle2" fontWeight="700">
                                            Room Images ({formData.images?.length || 0})
                                        </Typography>
                                    </Box>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        size="small"
                                        startIcon={isUploadingImages ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                        disabled={isUploadingImages || isLoadingSetup}
                                        sx={{ textTransform: 'none', borderRadius: 2 }}
                                    >
                                        {isUploadingImages ? 'Uploading...' : 'Upload Photos'}
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            multiple
                                            onChange={handleImageUpload}
                                        />
                                    </Button>
                                </Box>

                                {uploadError && (
                                    <FormHelperText error sx={{ mb: 1.5 }}>
                                        {uploadError}
                                    </FormHelperText>
                                )}

                                {formData.images && formData.images.length > 0 ? (
                                    <Box sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', 
                                        gap: 1.5,
                                        mt: 1.5
                                    }}>
                                        {formData.images.map((imgUrl, index) => (
                                            <Card 
                                                key={index} 
                                                sx={{ 
                                                    position: 'relative', 
                                                    borderRadius: 2, 
                                                    border: '1px solid #e2e8f0',
                                                    overflow: 'hidden',
                                                    height: 100,
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                                }}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    src={imgUrl}
                                                    alt={`Room ${formData.roomNumber} image ${index + 1}`}
                                                    sx={{ 
                                                        height: '100%', 
                                                        width: '100%', 
                                                        objectFit: 'cover' 
                                                    }}
                                                />
                                                {index === 0 && (
                                                    <Chip 
                                                        label="Cover" 
                                                        size="small" 
                                                        color="primary" 
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            top: 4, 
                                                            left: 4, 
                                                            height: 18, 
                                                            fontSize: '0.65rem',
                                                            fontWeight: 'bold',
                                                            px: 0.5
                                                        }} 
                                                    />
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveImage(index)}
                                                    disabled={isUploadingImages}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                                        color: '#ffffff',
                                                        padding: '3px',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(239, 68, 68, 0.9)'
                                                        }
                                                    }}
                                                >
                                                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Card>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        No images uploaded yet. Click "Upload Photos" to add room images (JPG, PNG, WEBP).
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button fullWidth variant="outlined" onClick={onClose} disabled={isLoadingSetup || isUploadingImages}>Cancel</Button>
                    <Button fullWidth variant="contained" onClick={handleSubmit} disabled={isLoadingSetup || isUploadingImages}>
                        {editingRoom ? 'Update Room' : 'Save Room'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
